import to from "await-to-js";
import { GetComponentProps } from "rc-table/lib/interface";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../components/Button/Button";
import { ActionsCell } from "../../../../components/CellRenderers/ActionsCell/ActionsCell";
import { Table } from "../../../../components/Table/Table";
import { SettingsContext } from "../../../../modules/settings/settings.context";
import {
  ICompanyUser,
  IUserDetails,
} from "../../../../modules/settings/settings.types";

import { ModalsContext } from "../../../../context/modals.context";
import { settingsService } from "../../../../modules/settings/settings.service";
import { CompanyUserModal } from "../CompanyUserModal/CompanyUserModal";

import { toastService } from "../../../../utils/toast.service";
import style from "./CompanyUsers.module.scss";

export const CompanyUsers = () => {
  const { settings, setSettings } = useContext(SettingsContext);
  const {
    handleUpdateModals,
    clearModals,
    setError,
    modals: { isLoading },
  } = useContext(ModalsContext);
  const { t } = useTranslation();

  const getCompanyUsers = async () => {
    const [err, res] = await to(settingsService.getCompanyUsers());

    if (err || !res.success) return setError(res?.errorMessage);

    setSettings({
      companyUsers: res.data.korisnici,
      obveznikRoles: res.data.role,
    });
  };

  const getUserDetails = async (userId?: string) => {
    if (!userId) return;
    const [err, res] = await to(settingsService.getUsersDetails(userId));
    if (err || !res.success) return setError(res?.errorMessage);
    setSettings({
      selectedUser: res.data.detaljiKorisnika,
    });
    handleUpdateModals({ modalVisible: "company_user" });
  };

  const createCompanyUser = async (user: IUserDetails) => {
    const [err, res] = await to(
      settingsService.addCompanyUser(settingsService.parseUserPayload(user))
    );
    if (err || !res.success) return setError(res?.errorMessage);

    await getCompanyUsers();

    clearModals();

    toastService.success("TOAST.CREATE_USER_SUCCESS");
  };

  const editCompanyUser = async (user: IUserDetails) => {
    const [err, res] = await to(
      settingsService.editCompanyUser(settingsService.parseUserPayload(user))
    );
    if (err || !res.success) return setError(res?.errorMessage);

    await getCompanyUsers();

    clearModals();

    toastService.success("TOAST.EDIT_USER_SUCCESS");
  };

  const clearUser = () => {
    setSettings({ selectedUser: null });
  };

  const handleCreate = () => {
    handleUpdateModals({ modalVisible: "company_user" });
  };

  const onRowHandler: GetComponentProps<ICompanyUser> = (row) => ({
    onClick: () => handleEdit(row),
  });

  const handleEdit = (companyUser: ICompanyUser) => {
    getUserDetails(companyUser.id);
  };

  const colDef = [
    {
      title: t("COMMON.IME_PREZIME"),
      render: (companyUser: ICompanyUser) =>
        `${companyUser.ime} ${companyUser.prezime}`,
    },
    { dataIndex: "emailAdress", title: t("COMMON.EMAIL") },
    { dataIndex: "uloga", title: t("COMMON.ULOGA") },
    { dataIndex: "role", title: t("COMMON.PERMISIJE") },
    { dataIndex: "status", title: t("TABLES.STATUS") },
    {
      title: t("COMMON.AKCIJE"),
      width: 120,
      render: (companyUser: ICompanyUser) => (
        <ActionsCell handleEdit={() => handleEdit(companyUser)} />
      ),
    },
  ];

  useEffect(() => {
    getCompanyUsers();
  }, []);

  return (
    <div className={style.container}>
      <CompanyUserModal
        selectedUser={settings?.selectedUser}
        createCompanyUser={createCompanyUser}
        editCompanyUser={editCompanyUser}
        clearUser={clearUser}
        roleGroups={settings?.obveznikRoles}
      />
      <div className={style.actions} id="user_actions">
        <Button name="add-object" type="primary" onClick={handleCreate}>
          {t("SETTINGS_USERS.NOVI_KORISNIK")}
        </Button>
      </div>
      <Table
        columns={colDef}
        loading={isLoading}
        scrollConfig={[".ant-tabs-nav", "#user_actions"]}
        dataSource={settings?.companyUsers}
        rowClassName="pointer"
        onRow={onRowHandler}
        rowKey="id"
        name="company-objects"
      />
    </div>
  );
};
